import { FormSectionHandler } from '../entity.handler';
import { Dictionary, ThemeResponse, ThemeUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ComboField, SearchSelectField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { KuiTreeSelectNode } from 'app/key-ui/tree-select/tree-select.component';
import { ICONS, getSvgIconNames, resolveIcon } from 'app/shared/icons';
import { unescapeRQLValue } from 'app/shared/utils/rql';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { escape } from 'lodash';
import { firstValueFrom } from 'rxjs';

// the weight combo's stand-in for "whatever the theme asks for", which writes no weight at all
const THEME_WEIGHT = 'default';

// the vocabulary is written in the order the application reads best, which is not one to look a
// name up in
const ICON_NAMES = Object.keys(ICONS).sort();

interface Substitution {
    icon: { id: string, name: string };
    substitute: { id: string, name: string };
    weight: string;
}

interface EditorValues {
    substitutions?: ListFieldValue<Substitution>[];
}

/**
 * The icons a theme draws in place of the application's own. This is a section of its own rather
 * than a settings group, because iconSubstitution sits at the root of the theme's settings and
 * ThemeSettingsBaseSectionHandler only reads and writes settings[group].
 */
export class ThemeIconsSectionHandler implements FormSectionHandler<ThemeResponse, EditorValues> {

    type = 'form';
    icon = 'theme-settings';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    private substituteTokens: Promise<string[]>;
    private substituted: string[] = [];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private httpClient: HttpClient,
        private sanitizer: DomSanitizer
    ) {
    }

    translate(key: string): string {
        return this.admin.translate(`EDITORS.THEME.ICONS.${key}`);
    }

    applyUpdate(entity: ThemeResponse, payload: ThemeUpdateRequest): Promise<ThemeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateTheme(entity.id, payload), { _type: 'theme', _cacheKey: `getTheme/${entity.id}` });
    }

    async mapEntityToEditor(entity: ThemeResponse): Promise<EditorValues> {
        const substitutions: Dictionary = entity.settings?.iconSubstitution || {};
        // a name the theme has already had removed is held as null, so it is not one of these
        this.substituted = Object.keys(substitutions).filter(name => substitutions[name]).sort();
        return {
            substitutions: this.substituted.map(name => this.toListItem(this.toSubstitution(name, substitutions[name]))),
        };
    }

    /**
     * The api merges a theme's settings rather than replacing them, which is what lets each section
     * send only its own. It also means dropping a key from this dictionary would leave the old
     * substitution in place, so a name that has been removed is written as null instead. The
     * resolver and this editor both read null as no substitution.
     */
    async mapEditorToEntity(entity: ThemeUpdateRequest, values: EditorValues): Promise<ThemeUpdateRequest> {
        const substitutions: Dictionary = {};
        this.substituted.forEach(name => substitutions[name] = null);
        (values.substitutions || []).forEach(item => {
            substitutions[item.data.icon.id] = this.toToken(item.data);
        });
        entity.settings = {
            ...entity.settings,
            iconSubstitution: substitutions,
        };
        return entity;
    }

    async getFields(_entity: ThemeResponse): Promise<FormBuilderField[]> {
        return [{
            id: 'substitutions',
            title: this.translate('FIELDS.SUBSTITUTIONS'),
            type: 'list',
            required: false,
            getText: (_field: FormBuilderField, values: EditorValues) => this.buildSubstitutionList(values.substitutions),
            actions: [
                { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showSubstitutionModal.bind(this) },
                { id: 'edit', click: this.showSubstitutionModal.bind(this) },
            ],
        }];
    }

    showSubstitutionModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<Substitution>): Promise<Substitution> {

        const translate = (key: string) => this.translate(`MODAL.${key}`);

        return this.admin.showFormModal<Substitution>({
            groups: [{
                name: translate('TITLE'),
                description: translate('DESC'),
                fields: [
                    SearchSelectField('icon', translate('FIELDS.ICON'), true, {
                        searchItemPromise: async id => id ? { id, name: id, icon: this.toDefaultGlyph(id) } : null,
                        searchListPromise: (limit, filter) => this.findIcons(values, item, limit, filter),
                    }),
                    SearchSelectField('substitute', translate('FIELDS.SUBSTITUTE'), true, {
                        searchItemPromise: async id => id ? { id, name: id, icon: id } : null,
                        searchListPromise: (limit, filter) => this.findSubstitutes(limit, filter),
                    }),
                    ComboField('weight', translate('FIELDS.WEIGHT'), true, [
                        { key: THEME_WEIGHT, value: translate('VALUES.WEIGHT_THEME') },
                        { key: '300', value: translate('VALUES.WEIGHT_LIGHT') },
                        { key: '400', value: translate('VALUES.WEIGHT_REGULAR') },
                        { key: '900', value: translate('VALUES.WEIGHT_SOLID') },
                    ]),
                ],
            }],
        }, item?.data || {
            icon: null,
            substitute: null,
            weight: THEME_WEIGHT,
        }).then(result => {
            if (result) {
                this.updateOrAddItem(values.substitutions, item, result);
            }
            return result;
        });

    }

    /**
     * The icon names still available to substitute. Names already in the list are left out so that
     * a second entry can't be made for one, which would silently lose the first.
     */
    async findIcons(values: EditorValues, item: ListFieldValue<Substitution>, limit: number, filter: string): Promise<{ items: KuiTreeSelectNode[], count: number }> {
        const term = filter ? unescapeRQLValue(filter).toLowerCase() : '';
        const used = (values.substitutions || []).map(x => x.id).filter(id => id !== item?.id);
        const names = ICON_NAMES.filter(name => !used.includes(name) && name.includes(term));
        return {
            count: names.length,
            items: names.slice(0, limit).map(name => ({ id: name, name: name, icon: this.toDefaultGlyph(name) })),
        };
    }

    /**
     * What an icon name draws before any substitution. A bare name would resolve through the theme
     * being edited, so the icon a vendor is about to replace would already show its replacement.
     */
    toDefaultGlyph(name: string): string {
        return `fa:${ICONS[name]}`;
    }

    /**
     * The section's read only view: one line per substitution, each drawing the replacement beside
     * the name it answers to. It is html rather than a component because the editor renders a
     * display value through innerHTML, which angular never compiles.
     */
    buildSubstitutionList(items: ListFieldValue<Substitution>[]): SafeHtml {
        const html = (items || []).map(item => `<div>${this.buildIcon(item.icon)}${escape(item.name)}</div>`).join('');
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    /**
     * Artwork cannot be written as a class, so it is inlined and sized here the way kui-icon sizes
     * it — that component's styles are scoped to itself and cannot reach markup the editor injects.
     * The markup is our own, from svg-icons.ts, but a glyph comes from the theme, so it is stripped
     * to what a font awesome name may contain before it goes anywhere near an attribute.
     */
    buildIcon(token: string): string {
        const resolved = resolveIcon(token);

        if (resolved.kind === 'art') {
            // artwork is drawn light and thickened with a stroke, so without one it renders thinner
            // than the glyphs beside it. the theme's own width is the fallback
            const svg = `<svg width="1em" height="1em" style="stroke:currentColor;stroke-linejoin:round;stroke-width:${resolved.strokeWidth || 'var(--icon-art-stroke,0)'}"`;
            return `<span class="mr-2" style="display:inline-flex;width:1em;height:1em;vertical-align:-.125em">${resolved.markup.replace('<svg', svg)}</span>`;
        }

        // a glyph takes the theme's --icon-weight from the icon class unless the token asked for one
        const weight = resolved.fontWeight ? ` style="font-weight:${resolved.fontWeight}"` : '';
        return `<i class="icon icon-${resolved.glyph.replace(/[^a-z0-9-]/g, '')} icon-fw mr-2"${weight}></i>`;
    }

    /** The artwork we ship, then every font awesome glyph the stylesheet can draw. */
    async findSubstitutes(limit: number, filter: string): Promise<{ items: KuiTreeSelectNode[], count: number }> {
        const term = filter ? unescapeRQLValue(filter).toLowerCase() : '';
        const tokens = (await this.getSubstituteTokens()).filter(token => token.includes(term));
        return {
            count: tokens.length,
            items: tokens.slice(0, limit).map(token => ({ id: token, name: token, icon: token })),
        };
    }

    /**
     * Everything a name can be substituted with, sorted within each kind and built once. The glyph
     * list is fetched the first time a picker opens rather than bundled — it is four thousand names.
     */
    getSubstituteTokens(): Promise<string[]> {
        this.substituteTokens = this.substituteTokens || firstValueFrom(this.httpClient.get<string[]>('assets/icons/fontawesome.json'))
            .then(glyphs => [
                ...getSvgIconNames().sort().map(name => `svg:${name}`),
                ...[...glyphs].sort().map(name => `fa:${name}`),
            ]);
        return this.substituteTokens;
    }

    toSubstitution(name: string, token: string): Substitution {
        const [kind, value, weight] = (token || '').split(':');
        return {
            icon: { id: name, name: name },
            substitute: { id: `${kind}:${value}`, name: `${kind}:${value}` },
            weight: weight || THEME_WEIGHT,
        };
    }

    toToken(substitution: Substitution): string {
        const token = substitution.substitute.id;
        return substitution.weight === THEME_WEIGHT ? token : `${token}:${substitution.weight}`;
    }

    toListItem(substitution: Substitution): ListFieldValue<Substitution> {
        const token = this.toToken(substitution);
        return {
            id: substitution.icon.id,
            name: substitution.icon.id,
            // the replacement itself, so the row draws what the theme will draw without the
            // substitution having been registered yet
            icon: token,
            description: token,
            data: substitution,
        };
    }

    updateOrAddItem(items: ListFieldValue<Substitution>[], existingItem: ListFieldValue<Substitution>, item: Substitution) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.toListItem(item);
        } else {
            items.push(this.toListItem(item));
        }
        // in place, because the list field holds on to the array it was given
        items.sort((a, b) => a.name.localeCompare(b.name));
    }

}
